import { useState, useEffect, useContext } from "react";
import { toast } from "sonner";
import { Stepper, useSteps } from "@/components/ui/stepper";
import { Button } from "@/components/ui/button";
import MainSectionSection from "../MainSectionSection";
import MerchantContext from "../../contexts/MerchantContext";
import { trpc } from "../../trpc";
import { getSystemPromptPreview } from "../../utils/rentalsSystemPrompt";
import {
  getCaptacionSystemPromptPreview,
  CaptacionAgentFormData,
} from "../../utils/captacionSystemPrompt";
import {
  getMaintenanceSystemPromptPreview,
  MaintenanceAgentFormData,
} from "../../utils/maintenanceSystemPrompt";
import {
  getGeneralSystemPromptPreview,
  GeneralAgentFormData,
} from "../../utils/generalSystemPrompt";
import {
  getDevelopmentSystemPromptPreview,
  DevelopmentAgentFormData,
} from "../../utils/developmentSystemPrompt";
import Step1AgentTypeSelection, { AgentType } from "./steps/Step1AgentTypeSelection";
import Step2AgentConfiguration, { AgentFormData } from "./steps/Step2AgentConfiguration";
import Step2CaptacionConfiguration from "./steps/Step2CaptacionConfiguration";
import Step2MaintenanceConfiguration from "./steps/Step2MaintenanceConfiguration";
import Step2GeneralConfiguration from "./steps/Step2GeneralConfiguration";
import Step2DevelopmentConfiguration from "./steps/Step2DevelopmentConfiguration";
import Step2CustomConfiguration, { CustomAgentFormData } from "./steps/Step2CustomConfiguration";
import Step2CarteraConfiguration, {
  CarteraAgentFormData,
} from "./steps/Step2CarteraConfiguration";
import Step3AgentConfirmation from "./steps/Step3AgentConfirmation";
import { TimeValue } from "./steps/TimeSelector";
import {
  AGENT_TOPIC_COLLECTIONS,
  AGENT_TOPIC_CUSTOM,
  AGENT_TOPIC_INQUIRIES,
  AGENT_TOPIC_MAINTENANCE,
  AGENT_TOPIC_SALES,
  AGENT_TOPIC_SOURCEPROPERTIES,
  PIPELINE_ID_COLLECTIONS,
  PIPELINE_ID_DEFAULT,
  PIPELINE_ID_GENERAL,
  PIPELINE_ID_MAINTENANCE,
  PIPELINE_ID_SOURCEPROPERTIES,
} from "../../../../agent/src/common/types";
import type { ToolType } from "../../types";

type CreateAgentFlowProps = {
  onCancel: () => void;
  onSuccess: () => void;
};

const CreateAgentFlow = ({ onCancel, onSuccess }: CreateAgentFlowProps) => {
  const { merchantId, displayName, merchantType } = useContext(MerchantContext);

  const [selectedAgentType, setSelectedAgentType] = useState<AgentType | null>(null);

  const DEFAULT_RENTALS_TOOLS: Array<
    | "transferConversation"
    | "scheduleVisit"
    | "getAvailableSlots"
    | "getProperty"
    | "getPropertyByCode"
    | "searchProperties"
    | "assignAgent"
    | "registerListingRequest"
    | "discardLead"
    | "calculateDaysFor"
    | "setAppointmentIntention"
  > = [
    "transferConversation",
    "scheduleVisit",
    "getAvailableSlots",
    "getProperty",
    "getPropertyByCode",
    "searchProperties",
    "setAppointmentIntention",
  ];
  const DEFAULT_SOURCEPROPERTIES_TOOLS: Array<
    | "transferConversation"
    | "scheduleVisit"
    | "getProperty"
    | "getPropertyByCode"
    | "searchProperties"
    | "assignAgent"
    | "registerListingRequest"
    | "discardLead"
    | "calculateDaysFor"
  > = ["transferConversation", "registerListingRequest"];

  const [agentFormData, setAgentFormData] = useState<AgentFormData>({
    agentName: "",
    politicaTratamientoDeDatos: "",
    greetingsMessage: `¡Hola! Bienvenido a ${
      displayName || "{merchantDisplayName}"
    }. Soy {nombreAgente}, y te ayudaré a encontrar tu lugar ideal para tu hogar o negocio. 😊 Al continuar con nuestra conversación, aceptas nuestra política de manejo de datos, disponible en {politicaTratamientoDeDatos} ¡Comencemos!`,
    coveredAreas: "Área Metropolitana de Medellín y otros municipios de Antioquia en Colombia",
    webPage: "",
    rentalsRequirements: `Estos son los requisitos:
- Debes realizar un estudio a través de nuestra aseguradora.
- No tener reportes en centrales de riesgo.
- Tus ingresos deben ser el doble de lo que pagas de arrendamiento.
- Debes contar con 1 o 2 codeudores que cumplan con los mismos requisitos anteriores.`,
    weekDayStart: { hour: "8", minute: "00", period: "AM" },
    weekDayEnd: { hour: "6", minute: "00", period: "PM" },
    visitOnSaturdayAllowed: false,
    saturdayDayStart: { hour: "8", minute: "00", period: "AM" },
    saturdayDayEnd: { hour: "2", minute: "00", period: "PM" },
    visitOnSundayAllowed: false,
    sundayDayStart: { hour: "9", minute: "00", period: "AM" },
    sundayDayEnd: { hour: "1", minute: "00", period: "PM" },
    minimumAdvanceHoursSales: undefined,
    minimumAdvanceHoursRent: undefined,
    selectedTools: DEFAULT_RENTALS_TOOLS,
  });

  const [captacionAgentFormData, setCaptacionAgentFormData] = useState<CaptacionAgentFormData>({
    agentName: "",
    politicaTratamientoDeDatos: "",
    coveredAreas: "Área Metropolitana de Medellín y otros municipios de Antioquia en Colombia",
    webPage: "",
    costDescription: `- Arriendo: administración del 10% + IVA sobre el canon (solo si el inmueble es arrendado); promoción y captación sin costo.

  - Venta: promoción y captación sin costo; comisión del 3% + IVA sobre el valor total de venta.`,
    requirements:
      "El asesor que te contactará, te dará más información y te acompañará en los pasos a seguir",
    selectedTools: DEFAULT_SOURCEPROPERTIES_TOOLS,
  });

  const [maintenanceAgentFormData, setMaintenanceAgentFormData] =
    useState<MaintenanceAgentFormData>({
      agentName: "",
      politicaTratamientoDeDatos: "",
      webPage: "",
      selectedTools: ["transferConversation", "createMaintenanceRequest"],
    });

  const [generalAgentFormData, setGeneralAgentFormData] = useState<GeneralAgentFormData>({
    agentName: "",
    politicaTratamientoDeDatos: "",
    webPage: "",
    selectedTools: ["transferConversation"],
  });

  const [customAgentFormData, setCustomAgentFormData] = useState<CustomAgentFormData>({
    agentName: "",
    pipelineId: "",
    pipelineDisplayName: "",
    pendingPositions: [],
    successPositions: [],
    failurePositions: [],
    systemPrompt: "",
    selectedTools: ["transferConversation", "updatePipelinePosition"],
  });

  const [carteraAgentFormData, setCarteraAgentFormData] = useState<CarteraAgentFormData>({
    agentName: "",
    systemPrompt: "",
    pendingPositions: [],
    successPositions: [],
    failurePositions: [],
    selectedTools: ["updatePipelinePosition", "transferConversation"],
  });

  const [developmentAgentFormData, setDevelopmentAgentFormData] =
    useState<DevelopmentAgentFormData>({
      agentName: "",
      politicaTratamientoDeDatos: "",
      greetingsMessage: `¡Hola! Bienvenido a ${
        displayName || "{merchantDisplayName}"
      }. Soy {nombreAgente}, y te ayudaré a conocer nuestros proyectos. 😊 Al continuar con nuestra conversación, aceptas nuestra política de manejo de datos, disponible en {politicaTratamientoDeDatos}. ¡Comencemos!`,
      coveredAreas: "",
      webPage: "",
      selectedTools: [
        "transferConversation",
        "getProject",
        "searchProjects",
        "typeOfProject",
        "scheduleVisit",
        "setAppointmentIntention",
        "assignAgent",
        "updateLeadProfile",
        "discardLead",
      ],
    });

  const [finalSystemPrompt, setFinalSystemPrompt] = useState("");

  const steps = [
    { title: "Tipo de Agente", description: "Selecciona el tipo de agente" },
    { title: "Configuración", description: "Configura los detalles del agente" },
    { title: "Confirmación", description: "Revisa y edita las instrucciones del agente" },
  ];

  const { activeStep, setActiveStep } = useSteps({
    count: steps.length,
  });

  const { data: merchant } = trpc.merchants.get.useQuery(
    { id: merchantId },
    { enabled: !!merchantId }
  );

  const { data: existingAgents } = trpc.agents.list.useQuery(
    { merchantId },
    { enabled: !!merchantId }
  );

  const utils = trpc.useUtils();

  // Determine which agent types are already taken (pipelineId first, then topic fallback)
  const hasComercialAgent = existingAgents?.some(
    (agent) => agent.pipelineId === PIPELINE_ID_DEFAULT || agent.topic === AGENT_TOPIC_SALES
  );
  const hasCaptacionAgent = existingAgents?.some(
    (agent) =>
      agent.pipelineId === PIPELINE_ID_SOURCEPROPERTIES ||
      agent.topic === AGENT_TOPIC_SOURCEPROPERTIES
  );
  const hasMantenimientosAgent = existingAgents?.some(
    (agent) =>
      agent.pipelineId === PIPELINE_ID_MAINTENANCE || agent.topic === AGENT_TOPIC_MAINTENANCE
  );
  const hasAdministrativoAgent = existingAgents?.some(
    (agent) => agent.pipelineId === PIPELINE_ID_GENERAL || agent.topic === AGENT_TOPIC_INQUIRIES
  );
  const hasCarteraAgent = existingAgents?.some(
    (agent) =>
      agent.pipelineId === PIPELINE_ID_COLLECTIONS || agent.topic === AGENT_TOPIC_COLLECTIONS
  );

  // Update greeting message when displayName becomes available
  useEffect(() => {
    if (displayName && agentFormData.greetingsMessage.includes("{merchantDisplayName}")) {
      setAgentFormData((prev) => ({
        ...prev,
        greetingsMessage: prev.greetingsMessage.replace(/{merchantDisplayName}/g, displayName),
      }));
    }
  }, [displayName, agentFormData.greetingsMessage]);

  // Same greeting placeholder substitution for the development flow
  useEffect(() => {
    if (
      displayName &&
      developmentAgentFormData.greetingsMessage.includes("{merchantDisplayName}")
    ) {
      setDevelopmentAgentFormData((prev) => ({
        ...prev,
        greetingsMessage: prev.greetingsMessage.replace(/{merchantDisplayName}/g, displayName),
      }));
    }
  }, [displayName, developmentAgentFormData.greetingsMessage]);

  const getDisplayGreetingMessageForDevelopment = () => {
    return developmentAgentFormData.greetingsMessage
      .replace(/{nombreAgente}/g, developmentAgentFormData.agentName || "{nombreAgente}")
      .replace(
        /{politicaTratamientoDeDatos}/g,
        developmentAgentFormData.politicaTratamientoDeDatos || "{politicaTratamientoDeDatos}"
      );
  };

  const getDisplayGreetingMessage = () => {
    return agentFormData.greetingsMessage
      .replace(/{nombreAgente}/g, agentFormData.agentName || "{nombreAgente}")
      .replace(
        /{politicaTratamientoDeDatos}/g,
        agentFormData.politicaTratamientoDeDatos || "{politicaTratamientoDeDatos}"
      );
  };

  const formatTimeForPrompt = (timeObj: TimeValue) => {
    return `${timeObj.hour}:${timeObj.minute} ${timeObj.period}`;
  };

  const handleAgentCatalogSelect = (agentType: AgentType) => {
    setSelectedAgentType(agentType);
    setActiveStep(1);
  };

  const handleNextStep = () => {
    if (activeStep < steps.length - 1) {
      // If moving from step 2 (Configuration) to step 3 (Confirmation), validate form data and create appointment config
      if (activeStep === 1) {
        if (selectedAgentType === "comercial") {
          // Validation for commercial agent step 2 (Configuration)
          if (
            !agentFormData.agentName.trim() ||
            !agentFormData.politicaTratamientoDeDatos.trim() ||
            !agentFormData.webPage.trim()
          ) {
            toast.error("Campos requeridos", {
              description:
                "Por favor completa el nombre del agente, enlace a la política de tratamiento de datos y la página web",
            });
            return;
          }

          // Create appointment config with time values
          const appointmentConfig: {
            merchantId: string;
            weekDayStart: string;
            weekDayEnd: string;
            visitOnSaturdayAllowed?: boolean;
            saturdayDayStart?: string;
            saturdayDayEnd?: string;
            visitOnSundayAllowed?: boolean;
            sundayDayStart?: string;
            sundayDayEnd?: string;
            minimumAdvanceHoursSales?: number;
            minimumAdvanceHoursRent?: number;
          } = {
            merchantId,
            weekDayStart: formatTimeForPrompt(agentFormData.weekDayStart),
            weekDayEnd: formatTimeForPrompt(agentFormData.weekDayEnd),
            visitOnSaturdayAllowed: agentFormData.visitOnSaturdayAllowed,
            visitOnSundayAllowed: agentFormData.visitOnSundayAllowed,
            minimumAdvanceHoursSales: agentFormData.minimumAdvanceHoursSales,
            minimumAdvanceHoursRent: agentFormData.minimumAdvanceHoursRent,
          };

          if (
            agentFormData.visitOnSaturdayAllowed &&
            agentFormData.saturdayDayStart &&
            agentFormData.saturdayDayEnd
          ) {
            appointmentConfig.saturdayDayStart = formatTimeForPrompt(
              agentFormData.saturdayDayStart
            );
            appointmentConfig.saturdayDayEnd = formatTimeForPrompt(agentFormData.saturdayDayEnd);
          }

          if (
            agentFormData.visitOnSundayAllowed &&
            agentFormData.sundayDayStart &&
            agentFormData.sundayDayEnd
          ) {
            appointmentConfig.sundayDayStart = formatTimeForPrompt(agentFormData.sundayDayStart);
            appointmentConfig.sundayDayEnd = formatTimeForPrompt(agentFormData.sundayDayEnd);
          }

          createAppointmentConfigMutation.mutate(appointmentConfig);

          // Generate the final prompt for step 3
          const generatedPrompt = getSystemPromptPreview({
            agentFormData,
            displayName: displayName || "",
            greetingsMessage: getDisplayGreetingMessage(),
            enableContactEmployeeButton: merchant?.enableContactEmployeeButton,
            calComIntegrationEnabled: merchant?.calComIntegrationEnabled,
          });
          setFinalSystemPrompt(generatedPrompt);
        } else if (selectedAgentType === "captacion") {
          // Validation for captacion agent step 2 (Configuration)
          if (
            !captacionAgentFormData.agentName.trim() ||
            !captacionAgentFormData.politicaTratamientoDeDatos.trim() ||
            !captacionAgentFormData.webPage.trim()
          ) {
            toast.error("Campos requeridos", {
              description:
                "Por favor completa el nombre del agente, enlace a la política de tratamiento de datos y la página web",
            });
            return;
          }

          // Generate the final prompt for step 3
          const generatedPrompt = getCaptacionSystemPromptPreview({
            agentFormData: captacionAgentFormData,
            displayName: displayName || "",
          });
          setFinalSystemPrompt(generatedPrompt);
        } else if (selectedAgentType === "mantenimientos") {
          // Validation for maintenance agent step 2 (Configuration)
          if (
            !maintenanceAgentFormData.agentName.trim() ||
            !maintenanceAgentFormData.politicaTratamientoDeDatos.trim() ||
            !maintenanceAgentFormData.webPage.trim()
          ) {
            toast.error("Campos requeridos", {
              description:
                "Por favor completa el nombre del agente, enlace a la política de tratamiento de datos y la página web",
            });
            return;
          }

          // Generate the final prompt for step 3
          const generatedPrompt = getMaintenanceSystemPromptPreview({
            agentFormData: maintenanceAgentFormData,
            displayName: displayName || "",
          });
          setFinalSystemPrompt(generatedPrompt);
        } else if (selectedAgentType === "administrativo") {
          // Validation for general agent step 2 (Configuration)
          if (
            !generalAgentFormData.agentName.trim() ||
            !generalAgentFormData.politicaTratamientoDeDatos.trim() ||
            !generalAgentFormData.webPage.trim()
          ) {
            toast.error("Campos requeridos", {
              description:
                "Por favor completa el nombre del agente, enlace a la política de tratamiento de datos y la página web",
            });
            return;
          }

          // Generate the final prompt for step 3
          const generatedPrompt = getGeneralSystemPromptPreview({
            agentFormData: generalAgentFormData,
            displayName: displayName || "",
          });
          setFinalSystemPrompt(generatedPrompt);
        } else if (selectedAgentType === "cartera") {
          if (!carteraAgentFormData.agentName.trim()) {
            toast.error("Campos requeridos", {
              description: "Por favor completa el nombre del agente",
            });
            return;
          }
          setFinalSystemPrompt(carteraAgentFormData.systemPrompt?.trim() ?? "");
        } else if (selectedAgentType === "development-comercial") {
          // Validation for development comercial agent step 2. Project + WhatsApp linking
          // happens after creation, from the agent management screen.
          if (
            !developmentAgentFormData.agentName.trim() ||
            !developmentAgentFormData.politicaTratamientoDeDatos.trim() ||
            !developmentAgentFormData.webPage.trim()
          ) {
            toast.error("Campos requeridos", {
              description:
                "Por favor completa el nombre del agente, enlace a la política de tratamiento de datos y la página web",
            });
            return;
          }

          const generatedPrompt = getDevelopmentSystemPromptPreview({
            agentFormData: {
              ...developmentAgentFormData,
              greetingsMessage: getDisplayGreetingMessageForDevelopment(),
            },
            displayName: displayName || "",
          });
          setFinalSystemPrompt(generatedPrompt);
        } else if (selectedAgentType === "custom") {
          if (
            !customAgentFormData.agentName.trim() ||
            !customAgentFormData.pipelineId.trim() ||
            !customAgentFormData.pipelineDisplayName.trim() ||
            !customAgentFormData.systemPrompt.trim()
          ) {
            toast.error("Campos requeridos", {
              description:
                "Por favor completa el nombre del agente, ID del proceso, nombre del proceso e instrucciones del agente",
            });
            return;
          }
          const reserved = ["default", "sourceproperties", "maintenance", "general", "collections"];
          if (reserved.includes(customAgentFormData.pipelineId.toLowerCase())) {
            toast.error("ID reservado", {
              description: `El ID "${customAgentFormData.pipelineId}" está reservado. Usa otro (ej: ventas-norte)`,
            });
            return;
          }
          setFinalSystemPrompt(customAgentFormData.systemPrompt.trim());
        }
      }

      setActiveStep(activeStep + 1);
    }
  };

  const handlePrevStep = () => {
    if (activeStep > 0) {
      setActiveStep(activeStep - 1);
    }
  };

  const handleAgentFormSubmit = () => {
    if (!selectedAgentType) {
      toast.error("Tipo de agente requerido", {
        description: "Por favor selecciona un tipo de agente",
      });
      return;
    }

    const agentName =
      selectedAgentType === "comercial"
        ? agentFormData.agentName
        : selectedAgentType === "captacion"
        ? captacionAgentFormData.agentName
        : selectedAgentType === "mantenimientos"
        ? maintenanceAgentFormData.agentName
        : selectedAgentType === "administrativo"
        ? generalAgentFormData.agentName
        : selectedAgentType === "cartera"
        ? carteraAgentFormData.agentName
        : selectedAgentType === "development-comercial"
        ? developmentAgentFormData.agentName
        : customAgentFormData.agentName;

    const agentTypeMap: Record<Exclude<AgentType, "custom" | "cartera">, string> = {
      comercial: AGENT_TOPIC_SALES,
      captacion: AGENT_TOPIC_SOURCEPROPERTIES,
      mantenimientos: AGENT_TOPIC_MAINTENANCE,
      administrativo: AGENT_TOPIC_INQUIRIES,
      // Development comercial agents reuse the rentals-sales topic — they live in the
      // default pipeline. The differentiator is the LangGraph graph (development-v2)
      // and the linked projectId, both wired on the backend.
      "development-comercial": AGENT_TOPIC_SALES,
    };

    const selectedTools: ToolType[] =
      selectedAgentType === "comercial"
        ? agentFormData.selectedTools
        : selectedAgentType === "captacion"
        ? captacionAgentFormData.selectedTools
        : selectedAgentType === "mantenimientos"
        ? maintenanceAgentFormData.selectedTools
        : selectedAgentType === "administrativo"
        ? generalAgentFormData.selectedTools
        : selectedAgentType === "cartera"
        ? carteraAgentFormData.selectedTools
        : selectedAgentType === "development-comercial"
        ? developmentAgentFormData.selectedTools
        : customAgentFormData.selectedTools;

    // Filter out tools that are not supported by the backend
    const supportedTools = [
      "transferConversation",
      "updatePipelinePosition",
      "requestRentGuaranteePreApproval",
      "scheduleVisit",
      "getAvailableSlots",
      "getProperty",
      "getPropertyByCode",
      "searchProperties",
      "assignAgent",
      "setEmail",
      "registerListingRequest",
      "discardLead",
      "calculateDaysFor",
      "createMaintenanceRequest",
      "getCustomerProperties",
      "getFloorPlan",
      "createServiceRequest",
      "getCustomerDocuments",
      "lookupByContractNumber",
      "setAppointmentIntention",
      "sendMedia",
      // Development-v2 tools
      "getProject",
      "searchProjects",
      "typeOfProject",
      "updateLeadProfile",
    ] as const;

    const filteredTools = selectedTools.filter((tool) =>
      supportedTools.includes(tool as (typeof supportedTools)[number])
    ) as Array<(typeof supportedTools)[number]>;

    if (selectedAgentType === "custom") {
      const pendingPositions = customAgentFormData.pendingPositions.filter(
        (p) => p.id.trim() && p.displayName.trim()
      );
      const successPositions = customAgentFormData.successPositions.filter(
        (p) => p.id.trim() && p.displayName.trim()
      );
      const failurePositions = customAgentFormData.failurePositions.filter(
        (p) => p.id.trim() && p.displayName.trim()
      );
      createAgentMutation.mutate({
        merchantId,
        systemPrompt: finalSystemPrompt!,
        agentName,
        agentTopic: AGENT_TOPIC_CUSTOM,
        tools: filteredTools,
        pipelineId: customAgentFormData.pipelineId.trim(),
        pipelineDisplayName: customAgentFormData.pipelineDisplayName.trim(),
        pendingPositions,
        successPositions,
        failurePositions,
      });
      return;
    }

    if (selectedAgentType === "cartera") {
      const pendingPositions = carteraAgentFormData.pendingPositions.filter(
        (p) => p.id.trim() && p.displayName.trim()
      );
      const successPositions = carteraAgentFormData.successPositions.filter(
        (p) => p.id.trim() && p.displayName.trim()
      );
      const failurePositions = carteraAgentFormData.failurePositions.filter(
        (p) => p.id.trim() && p.displayName.trim()
      );
      createAgentMutation.mutate({
        merchantId,
        systemPrompt: finalSystemPrompt ?? "",
        agentName,
        agentTopic: AGENT_TOPIC_COLLECTIONS,
        tools: filteredTools,
        pipelineId: "collections",
        pipelineDisplayName: "Cartera",
        pendingPositions,
        successPositions,
        failurePositions,
      });
      return;
    }

    createAgentMutation.mutate({
      merchantId,
      systemPrompt: finalSystemPrompt,
      agentName,
      agentTopic: agentTypeMap[selectedAgentType],
      tools: filteredTools,
      webPage:
        selectedAgentType === "comercial"
          ? agentFormData.webPage
          : selectedAgentType === "captacion"
          ? captacionAgentFormData.webPage
          : selectedAgentType === "mantenimientos"
          ? maintenanceAgentFormData.webPage
          : selectedAgentType === "development-comercial"
          ? developmentAgentFormData.webPage
          : generalAgentFormData.webPage,
    });
  };

  // Mutation for creating appointment config
  const createAppointmentConfigMutation = trpc.agents.createOrUpdateAppointmentConfig.useMutation({
    onError: (error) => {
      console.error("Error creating appointment config:", error);
      toast.error("Error al crear configuración de citas", {
        description: error.message || "Ocurrió un error al crear la configuración de citas",
      });
    },
  });

  // Mutation for creating agent
  const createAgentMutation = trpc.agents.createAgent.useMutation({
    onSuccess: () => {
      toast.success("Agente creado exitosamente", {
        description: "El agente ha sido creado y configurado correctamente",
      });
      // Invalidate and refetch agents list
      utils.agents.list.invalidate({ merchantId });
      onSuccess();
    },
    onError: (error) => {
      console.error("Error creating agent:", error);
      toast.error("Error al crear agente", {
        description: error.message || "Ocurrió un error al crear el agente",
      });
    },
  });

  return (
    <MainSectionSection gap="24px">
      <div className="flex flex-col gap-6">
        {/* Header with back button */}
        <div className="rounded-md bg-white p-6 shadow-[0px_4px_20px_1px_rgba(125,124,135,0.2)]">
          <h2 className="mb-6 text-xl font-bold">
            Crear Nuevo Agente
          </h2>

          {/* Stepper */}
          <Stepper
            activeStep={activeStep}
            steps={steps}
            size="md"
            className="mb-6 text-sm md:text-base"
          />
        </div>

        {/* Step Content */}
        <div className="min-h-[300px] rounded-md bg-white p-6 shadow-[0px_4px_20px_1px_rgba(125,124,135,0.2)] md:min-h-[500px]">
          {activeStep === 0 && (
            <Step1AgentTypeSelection
              onSelect={handleAgentCatalogSelect}
              merchantType={merchantType ?? undefined}
              disabledTypes={{
                comercial: hasComercialAgent || false,
                captacion: hasCaptacionAgent || false,
                mantenimientos: hasMantenimientosAgent || false,
                administrativo: hasAdministrativoAgent || false,
                cartera: hasCarteraAgent || false,
                custom: false,
              }}
            />
          )}

          {activeStep === 1 && selectedAgentType === "comercial" && (
            <Step2AgentConfiguration
              agentFormData={agentFormData}
              onFormDataChange={setAgentFormData}
              displayName={displayName || ""}
              enableContactEmployeeButton={merchant?.enableContactEmployeeButton}
              calComIntegrationEnabled={merchant?.calComIntegrationEnabled}
            />
          )}

          {activeStep === 1 && selectedAgentType === "captacion" && (
            <Step2CaptacionConfiguration
              agentFormData={captacionAgentFormData}
              onFormDataChange={setCaptacionAgentFormData}
              displayName={displayName || ""}
            />
          )}

          {activeStep === 1 && selectedAgentType === "mantenimientos" && (
            <Step2MaintenanceConfiguration
              agentFormData={maintenanceAgentFormData}
              onFormDataChange={setMaintenanceAgentFormData}
              displayName={displayName || ""}
            />
          )}

          {activeStep === 1 && selectedAgentType === "administrativo" && (
            <Step2GeneralConfiguration
              agentFormData={generalAgentFormData}
              onFormDataChange={setGeneralAgentFormData}
              displayName={displayName || ""}
            />
          )}

          {activeStep === 1 && selectedAgentType === "custom" && (
            <Step2CustomConfiguration
              formData={customAgentFormData}
              onFormDataChange={setCustomAgentFormData}
            />
          )}

          {activeStep === 1 && selectedAgentType === "cartera" && (
            <Step2CarteraConfiguration
              formData={carteraAgentFormData}
              onFormDataChange={setCarteraAgentFormData}
            />
          )}

          {activeStep === 1 && selectedAgentType === "development-comercial" && (
            <Step2DevelopmentConfiguration
              agentFormData={developmentAgentFormData}
              onFormDataChange={setDevelopmentAgentFormData}
              displayName={displayName || ""}
            />
          )}

          {activeStep === 2 && (
            <Step3AgentConfirmation
              systemPrompt={finalSystemPrompt}
              onSystemPromptChange={setFinalSystemPrompt}
            />
          )}

          {/* Action Buttons */}
          <div className="mt-8 flex justify-between">
            <Button variant="outline" onClick={activeStep === 0 ? onCancel : handlePrevStep}>
              {activeStep === 0 ? "Cancelar" : "Anterior"}
            </Button>

            <Button
              onClick={activeStep === 2 ? handleAgentFormSubmit : handleNextStep}
              disabled={createAgentMutation.isLoading}
            >
              {createAgentMutation.isLoading
                ? activeStep === 2
                  ? "Creando..."
                  : "Procesando..."
                : activeStep === 2
                ? "Crear Agente"
                : "Continuar"}
            </Button>
          </div>
        </div>
      </div>
    </MainSectionSection>
  );
};

export default CreateAgentFlow;
